import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Handshake, Users, ChartLineUp, CheckCircle, ArrowRight,
  Phone, Calendar, Shield, Star, Buildings, Medal,
  Target, Lightbulb, Gear, Heart
} from "@phosphor-icons/react";

const HospiceConsultingPage = () => {
  const services = [
    {
      title: "Compliance Consulting",
      description: "Ensure your hospice meets all CMS Conditions of Participation and state regulatory requirements.",
      icon: Shield,
      items: ["Gap analysis", "Policy development", "Survey preparation", "Ongoing compliance support"]
    },
    {
      title: "Operational Excellence",
      description: "Streamline operations, improve efficiency, and build sustainable processes that scale.",
      icon: Gear,
      items: ["Workflow optimization", "Staff training", "Process improvement", "Technology integration"]
    },
    {
      title: "Clinical Excellence",
      description: "Elevate patient care quality through evidence-based practices and clinical best practices.",
      icon: Heart,
      items: ["Care coordination", "Documentation excellence", "Quality metrics", "Clinical training"]
    },
    {
      title: "Growth Strategy",
      description: "Develop and execute strategies to grow your census and expand your market presence.",
      icon: ChartLineUp,
      items: ["Market analysis", "Referral development", "Community outreach", "Brand positioning"]
    }
  ];

  const whyChoose = [
    { stat: "100+", label: "Hospices Served" },
    { stat: "20+", label: "Years Experience" },
    { stat: "95%", label: "Client Satisfaction" },
    { stat: "Zero", label: "Survey Deficiencies Goal" }
  ];

  const testimonials = [
    {
      quote: "Noble transformed our hospice operations. We went from struggling with compliance to achieving a zero-deficiency survey.",
      author: "Administrator, 150-ADC Hospice"
    },
    {
      quote: "Their expertise in hospice consulting is unmatched. They understand the unique challenges we face and deliver real results.",
      author: "CEO, Regional Hospice Provider"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Hospice Consulting Services | Expert Hospice Consultants | Noble Hospice Solutions</title>
        <meta name="description" content="Premier hospice consulting services. Expert consultants helping hospice agencies with compliance, operations, clinical excellence, and growth strategy. Over 100 hospices served. Free consultation." />
        <meta name="keywords" content="hospice consulting, hospice consultant, hospice consulting services, hospice compliance consultant, hospice operations consulting, hospice business consulting, hospice management consulting" />
        <link rel="canonical" href="https://noblehospicesolutions.com/hospice-consulting" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Noble Hospice Solutions",
            "url": "https://noblehospicesolutions.com",
            "description": "Premier hospice consulting firm specializing in compliance, operations, and growth strategy.",
            "areaServed": "United States",
            "priceRange": "$$",
            "telephone": "+1-718-304-6214",
            "email": "noblehospicesolutions@gmail.com",
            "serviceType": ["Healthcare Consulting", "Hospice Consulting", "Compliance Consulting"]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 text-sm font-semibold mb-6">
              <Medal weight="fill" className="w-4 h-4" />
              Trusted by 100+ Hospices
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6">
              Hospice Consulting
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Where Excellence Meets Compassion
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Partner with the industry's leading hospice consultants. We help hospice agencies 
              achieve compliance excellence, operational efficiency, and sustainable growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Calendar weight="bold" className="w-5 h-5" />
                Free Consultation
              </Link>
              <a
                href="tel:+17183046214"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <Phone weight="bold" className="w-5 h-5" />
                Call (718) 304-6214
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {whyChoose.map((item, i) => (
              <div key={i}>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-1">
                  {item.stat}
                </div>
                <div className="text-slate-600 font-medium">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold mb-4">
              Comprehensive Hospice Consulting Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From compliance to growth, we provide end-to-end consulting services tailored to your hospice's unique needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-cyan-500/50 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-6">
                  <service.icon weight="fill" className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle weight="fill" className="w-4 h-4 text-cyan-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-4">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} weight="fill" className="w-5 h-5 text-amber-400" />
                  ))}
                </div>
                <p className="text-white text-lg italic mb-4">"{testimonial.quote}"</p>
                <p className="text-slate-400">— {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
            Ready to Transform Your Hospice?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Schedule a free consultation with our expert hospice consultants and discover how we can help you succeed.
          </p>
          <Link
            to="/schedule-demo"
            className="bg-white text-cyan-600 font-bold px-8 py-4 rounded-full hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
          >
            Get Your Free Consultation
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HospiceConsultingPage;
